import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextField, SelectField, CheckGroup, isEmail } from "@/components/forms/FormBits";
import { useAssessment } from "@/context/AssessmentContext";
import { submitAssessment } from "@/lib/api";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { propertyTypes, platformOptions, serviceOptions } from "@/data/content";
import { CheckCircle2, Loader2 } from "lucide-react";

const empty = {
  name: "", property_name: "", phone: "", email: "", city: "",
  property_type: "", rooms: "", website: "", message: "",
};

export default function AssessmentModal() {
  const { open, setOpen } = useAssessment();
  const [form, setForm] = useState(empty);
  const [platforms, setPlatforms] = useState([]);
  const [services, setServices] = useState([]);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (arr, setArr) => (v) => setArr((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.property_name.trim()) e.property_name = "Please provide your property name.";
    if (!form.phone.trim()) e.phone = "Please enter your phone / WhatsApp.";
    if (!isEmail(form.email)) e.email = "Please enter a valid email address.";
    if (!form.city.trim()) e.city = "Please enter your city.";
    if (!form.property_type) e.property_type = "Please select a property type.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await submitAssessment({ ...form, current_platforms: platforms, services });
      trackEvent(EVENTS.ASSESSMENT_SUBMITTED);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const reset = () => {
    setForm(empty); setPlatforms([]); setServices([]); setErrors({}); setStatus("idle");
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setTimeout(reset, 250);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl p-0" data-testid="assessment-modal">
        {status === "success" ? (
          <div className="flex flex-col items-center px-8 py-16 text-center" data-testid="assessment-success">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-ocean/10">
              <CheckCircle2 className="h-9 w-9 text-ocean" />
            </div>
            <h3 className="mt-6 font-serif text-2xl text-navy">Thank you!</h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600">
              Your property assessment request has been received. HM Services will review your information and contact you shortly.
            </p>
            <Button onClick={() => handleOpenChange(false)} className="mt-8 rounded-full bg-navy px-8 text-white hover:bg-navy-800" data-testid="assessment-success-close">Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader className="border-b border-line bg-mist/60 px-8 py-6 text-left">
              <DialogTitle className="font-serif text-2xl text-navy">Get a Free Property Assessment</DialogTitle>
              <DialogDescription className="text-sm text-slate-600">
                Tell us about your property and we'll recommend the right services for you.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={onSubmit} className="space-y-5 px-8 py-6" data-testid="assessment-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField id="a-name" label="Full Name" required value={form.name} onChange={set("name")} error={errors.name} placeholder="Your name" data-testid="assessment-name" />
                <TextField id="a-property" label="Property Name" required value={form.property_name} onChange={set("property_name")} error={errors.property_name} placeholder="e.g. Skyline Suites" data-testid="assessment-property" />
                <TextField id="a-phone" label="Phone / WhatsApp" required value={form.phone} onChange={set("phone")} error={errors.phone} placeholder="+92 ..." data-testid="assessment-phone" />
                <TextField id="a-email" label="Email" required type="email" value={form.email} onChange={set("email")} error={errors.email} placeholder="you@email.com" data-testid="assessment-email" />
                <TextField id="a-city" label="City" required value={form.city} onChange={set("city")} error={errors.city} placeholder="e.g. Lahore" data-testid="assessment-city" />
                <SelectField id="a-type" label="Property Type" required options={propertyTypes} value={form.property_type} onChange={set("property_type")} error={errors.property_type} placeholder="Select property type" data-testid="assessment-type" />
                <TextField id="a-rooms" label="Number of Rooms" value={form.rooms} onChange={set("rooms")} placeholder="Optional" data-testid="assessment-rooms" />
                <TextField id="a-website" label="Website / Social Media URL" value={form.website} onChange={set("website")} placeholder="Optional" data-testid="assessment-website" />
              </div>

              <CheckGroup label="Current Booking Platforms" options={platformOptions} values={platforms} onToggle={toggle(platforms, setPlatforms)} columns={3} testId="assessment-platforms" />
              <CheckGroup label="Services Required" options={serviceOptions} values={services} onToggle={toggle(services, setServices)} columns={2} testId="assessment-services" />

              <div>
                <label className="mb-1.5 block text-sm font-medium text-navy">Additional Message</label>
                <textarea rows={3} value={form.message} onChange={set("message")} placeholder="Tell us anything else about your property or goals..." className="w-full resize-none rounded-xl border border-line bg-white px-4 py-3 text-sm outline-none transition-[border-color,box-shadow] duration-200 focus:border-ocean focus:ring-4 focus:ring-ocean/10" data-testid="assessment-message" />
              </div>

              {status === "error" && (
                <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="assessment-error">Something went wrong. Please try again.</p>
              )}

              <Button type="submit" disabled={status === "loading"} className="w-full rounded-full bg-ocean py-6 text-base text-white hover:bg-royal" data-testid="assessment-submit">
                {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : "Request Free Assessment"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
